import React, { FC, useMemo, useRef, useState } from 'react';
import { Select } from 'antd';
import { uniq } from 'lodash';

import {
  getSplittedSearchValues,
  getTokenizedMatchedValues,
  getTokenizedUnmatchedValues,
  sortByExactMatch,
} from '../helper';
import InputControlProps from '../InputControlProps';
import ListInputDetectionMesssage from '@/common/ListInputDetectionMessage';

export interface MultiSelectDropdownV2Props extends InputControlProps {
  tokenizable?: boolean;
  optionFilterProp?: string;
  sorted?: boolean;
}

const MultiSelectDropdownV2: FC<MultiSelectDropdownV2Props> = ({
  tokenizable,
  optionFilterProp,
  sorted,
  onChange,
  value,
  availableValues,
  placeholder,
  disabled,
  multiple,
}) => {
  const [searchValue, setSearchValue] = useState<string>('');

  const selectComponentRef = useRef<any>(null);

  const onSearch = (searchValue: string) => {
    setSearchValue(searchValue);
  };

  const getKeyValuePairs = () => {
    let keyValuePairs = [];
    for (let key in availableValues) {
      let value = availableValues[key];
      keyValuePairs.push({ value, key });
    }
    return keyValuePairs;
  };

  const sortedKeyValuePairs = useMemo(() => {
    let keyValuePairs = getKeyValuePairs();
    if (sorted) {
      keyValuePairs = [...keyValuePairs].sort((a, b) =>
        a.value.localeCompare(b.value),
      );
    }
    return sortByExactMatch(keyValuePairs, searchValue, 'value');
  }, [availableValues, sorted, searchValue]);

  const renderOptions = () => {
    const options: React.ReactNode[] = [];
    sortedKeyValuePairs.forEach(({ value, key }) => {
      options.push(
        <Select.Option key={key} value={key}>
          {value}
        </Select.Option>,
      );
    });
    return options;
  };

  const renderDropdown = (menu) => {
    const isListInput =
      tokenizable &&
      searchValue.includes(',') &&
      getSplittedSearchValues(searchValue).length > 1;

    if (isListInput) {
      const keyValuePairs = getKeyValuePairs();
      const splittedSearchValues = getSplittedSearchValues(searchValue);
      const unmatchedValues = getTokenizedUnmatchedValues(
        splittedSearchValues,
        keyValuePairs,
        'value',
      );
      return <ListInputDetectionMesssage unmatchedValues={unmatchedValues} />;
    }
    return menu;
  };

  const onValueChanged = (value: string[] | string) => {
    if (Array.isArray(value)) onChange('[' + value.join(',') + ']');

    if (typeof value === 'string') onChange(value);
  };

  const parsePropsValue = () => {
    if (!value || value === '[]') {
      return [];
    }

    return value.startsWith('[') && value.endsWith(']')
      ? value.slice(1, -1).split(',')
      : [value.toString()];
  };

  const handleListSelect = (e: { keyCode: number }) => {
    if (!tokenizable) {
      return;
    }

    const isListInput =
      tokenizable &&
      searchValue.includes(',') &&
      getSplittedSearchValues(searchValue).length > 1;

    if (e.keyCode === 13 && isListInput) {
      const keyValuePairs = getKeyValuePairs();
      const splittedSearchValues = getSplittedSearchValues(searchValue);
      const matchedValues = getTokenizedMatchedValues(
        splittedSearchValues,
        keyValuePairs,
        'value',
      );
      const matchedOptionValues = keyValuePairs
        .filter(({ value }) =>
          matchedValues.some(
            (item) => item.toLowerCase() === value.toLowerCase(),
          ),
        )
        .map(({ key }) => key);

      const currentValues = parsePropsValue();
      let newValues = [...currentValues, ...matchedOptionValues];
      newValues = uniq(newValues);

      onValueChanged(newValues);
      setSearchValue('');
      selectComponentRef.current.blur();
    }
  };

  return (
    <Select
      style={{ width: '100%', minWidth: 200 }}
      mode={multiple ? 'multiple' : null}
      onChange={onValueChanged}
      onSearch={onSearch}
      placeholder={placeholder}
      disabled={disabled}
      dropdownRender={renderDropdown}
      value={parsePropsValue()}
      allowClear
      showSearch
      ref={selectComponentRef}
      onKeyDown={handleListSelect}
      optionFilterProp={optionFilterProp}
    >
      {renderOptions()}
    </Select>
  );
};

export default MultiSelectDropdownV2;
